import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Controla o menu lateral do cabeçalho.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onDrawerClick, true);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onDrawerClick, true);
  }

  /**
   * Informa se o menu principal está aberto.
   *
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Encontra o details relacionado ao evento.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    const details = event.target.closest('details');

    if (details instanceof HTMLDetailsElement) {
      return details;
    }

    return this.refs.details;
  }

  /**
   * Fecha o menu ao pressionar Escape.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#resetDrawer();
  };

  /**
   * Controla o clique no X, no hambúrguer e nos links.
   *
   * @param {MouseEvent} event
   */
  #onDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest(
      '.menu-drawer__close-button'
    );

    const mainSummary = event.target.closest(
      '#Details-menu-drawer-container > summary'
    );

    const link = event.target.closest(
      '.menu-drawer a[href]'
    );

    /*
     * Fecha pelo X localizado dentro da gaveta.
     */
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.#resetDrawer();
      return;
    }

    /*
     * Quando o menu está aberto, o ícone hambúrguer vira X.
     * O clique nesse X também fecha imediatamente.
     */
    if (mainSummary && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.#resetDrawer();
      return;
    }

    /*
     * Fecha o menu antes de navegar para outro destino.
     */
    if (link && this.contains(link)) {
      if (
        link.target === '_blank' ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      this.#resetDrawer();
    }
  };

  /**
   * Abre ou fecha o menu principal.
   */
  toggle() {
    if (this.isOpen) {
      this.#resetDrawer();
      return;
    }

    this.open();
  }

  /**
   * Abre o menu principal ou submenu relacionado ao evento.
   *
   * @param {Event} [event]
   */
   /**
   * Abre o menu principal ou submenu relacionado ao evento.
   *
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector(':scope > summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
    });

    trapFocus(details);
  }
  
  /**
   * Volta de um submenu ou fecha o menu relacionado ao evento.
   *
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getDetailsElement(event);

    this.#close(details);
  }

  /**
   * Fecha o menu principal.
   */
  close() {
    this.#resetDrawer();
  }

  /**
   * Fecha um details específico.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    if (!(details instanceof HTMLDetailsElement)) {
      this.#resetDrawer();
      return;
    }

    if (details === this.refs.details) {
      this.#resetDrawer();
      return;
    }

    details.classList.remove('menu-open');
    details.removeAttribute('open');

    details
      .querySelector(':scope > summary')
      ?.setAttribute('aria-expanded', 'false');

    trapFocus(this.refs.details);
  }

  /**
   * Fecha completamente a gaveta e libera a página.
   */
  #resetDrawer() {
    const allDetails = this.querySelectorAll('details');

    allDetails.forEach((details) => {
      details.classList.remove('menu-open');
      details.removeAttribute('open');

      details
        .querySelector(':scope > summary')
        ?.setAttribute('aria-expanded', 'false');
    });

    this.refs.details.classList.remove('menu-open');
    this.refs.details.removeAttribute('open');

    this.refs.details
      .querySelector(':scope > summary')
      ?.setAttribute('aria-expanded', 'false');

    removeTrapFocus();

    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');

    document.documentElement.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('padding-right');

    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('top');

    document.dispatchEvent(
      new CustomEvent('header-drawer:closed', {
        bubbles: true
      })
    );
  }

  /**
   * Remove o will-change depois das animações dos itens.
   */
  #setupAnimatedElementListeners() {
    const removeWillChangeOnAnimationEnd = (event) => {
      const target = event.target;

      if (!(target instanceof HTMLElement)) return;

      target.style.setProperty('will-change', 'unset');
      target.removeEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    };

    const animatedElements = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    animatedElements.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}